/**
 * API utilities for making requests to the backend
 */
import { isTokenExpired, handleTokenExpiration, createAuthHeaders, isTokenValid } from './auth';

// Base URL for API requests - ensure it matches backend server
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

/**
 * Get user profile data
 * @returns {Promise<any>} - User profile data
 */
export const getUserProfile = async () => {
  try {
    const response = await fetchWithAuth('/api/auth/me');
    
    if (!response.ok) {
      throw new Error(`Failed to fetch user profile: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error fetching user profile:', error);
    throw error;
  }
};

/**
 * Utility function for API requests with improved error handling
 * @param {string} url - The API endpoint to call
 * @param {Object} options - Fetch options
 * @returns {Promise<any>} - Parsed JSON response
 */
export const fetchApi = async (url, options = {}) => {
  // Ensure the URL has the correct base
  const fullUrl = url.startsWith('http') ? url : `${API_BASE_URL}${url}`;
  
  try {
    console.log(`Making API request to: ${fullUrl}`);
    
    // Check if token is valid before making the request
    if (options.headers?.Authorization && !isTokenValid()) {
      console.log('Token is expired, logging out before request');
      handleTokenExpiration();
      throw new Error('Token expired');
    }
    
    const response = await fetch(fullUrl, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
    });

    console.log(`API ${options.method || 'GET'} ${fullUrl} status:`, response.status);
    
    // Handle 401 Unauthorized errors (expired token)
    if (response.status === 401) {
      console.log('Received 401 response, checking for token expiration');
      const errorData = await response.json().catch(() => ({}));
      
      if (isTokenExpired(errorData) || errorData.error?.toLowerCase().includes('token')) {
        console.log('Token is expired according to server response');
        handleTokenExpiration();
        throw new Error('Token expired');
      }
    }
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error(`API request failed: ${error.message}`);
    
    // Check if the error is related to token expiration
    if (isTokenExpired(error)) {
      console.log('Error indicates token expiration');
      handleTokenExpiration();
    }
    
    throw error;
  }
};

export const fetchWithAuth = async (url, options = {}) => {
  // Use the createAuthHeaders utility from auth.js
  const headers = {
    ...createAuthHeaders(),
    ...options.headers
  };

  // Check token validity before making request
  if (!isTokenValid() && url !== '/api/auth/login') {
    console.log('Token is expired or invalid, logging out');
    handleTokenExpiration();
    throw new Error('Token expired');
  }

  // Use the base URL here too
  const fullUrl = url.startsWith('http') ? url : `${API_BASE_URL}${url}`;
  
  try {
    const response = await fetch(fullUrl, {
      ...options,
      headers
    });

    // Handle 401 Unauthorized errors more explicitly
    if (response.status === 401) {
      console.log('Unauthorized response from server');
      await response.json().catch(() => ({}));
      
      if (url !== '/api/auth/login') {
        handleTokenExpiration();
        throw new Error('Session expired. Please log in again.');
      }
    }

    return response;
  } catch (error) {
    console.error(`Auth API request failed: ${error.message}`);
    
    // Check if error is related to token expiration
    if (isTokenExpired(error) && url !== '/api/auth/login') {
      handleTokenExpiration();
    }
    
    throw error;
  }
}; 